import "./i18n";
import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import PublicHome from "./pages/PublicHome";
import Login from "./pages/Login";
import UserHome from "./pages/UserHome";
import PitchSessionStart from "./pages/PitchSessionStart";
import PitchSession from "./pages/PitchSession";
import Navbar from "./components/Navbar";
import VerifyEmail from "./pages/VerifyEmail";
import CheckAPI from "./components/CheckAPI";
import SignupForm from "./pages/SignupForm";
import ResetPassword from "./pages/ResetPassword";
import ActivateAccount from "./pages/ActivateAccount";
import ForgotPassword from "./pages/ForgotPassword";
import ProfileEdit from "./pages/ProfileEdit";
import Footer from "./components/Footer";
import ContactForm from "./pages/ContactForm";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import ScrollToTop from "./components/ScrollToTop";
import { SettingsProvider } from "./context/SettingsContext";
import EvaluationPage from "./pages/EvaluationPage";
import Dashboard from "./pages/Dashboard";
import Sessions from "./pages/Sessions";
import Pricing from "./pages/Pricing";
import Checkout from "./pages/Checkout";
import CheckoutResult from "./pages/CheckoutResult";
import Billing from "./pages/Billing";
import Faqs from "./pages/Faqs";
import EarlyPitchers from "./pages/EarlyPitchers";
import AchievementsPage from "./pages/AchievementsPage";
import AdminDashboard from "./pages/AdminDashboard";
import AdminContactMessages from "./pages/AdminContactMessages";
import AdminEarlyPitchers from "./pages/AdminEarlyPitchers";
import AdminPurchases from "./pages/AdminPurchases";
import AdminUsers from "./pages/AdminUsers";
import AdminSessions from "./pages/AdminSessions";
import AdminPayments from "./pages/AdminPayments";
import AdminSessionRefunds from "./pages/AdminSessionRefunds";
import AdminDiscountAssignments from "./pages/AdminDiscountAssignments";
import AdminSettings from "./pages/AdminSettings";
import AppLoader from "./components/AppLoader";

import { useState, useEffect } from "react";
import { Box, Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "./hooks/AuthContext";

const AppLayout = () => {
  const location = useLocation();
  const hideNavbar = location.pathname.startsWith("/verify-email");
  const hideFooter = location.pathname.startsWith("/verify-email");

  const { t } = useTranslation("common");
  const [tokenExpired, setTokenExpired] = useState(false);
  const { logout, isAuthenticated, authLoading } = useAuth();

  useEffect(() => {
    const handle401 = () => {
      setTokenExpired(true);
      setTimeout(() => {
        logout();
        window.location.href = "/login";
      }, 2500);
    };

    window.addEventListener("tokenExpired", handle401);
    return () => window.removeEventListener("tokenExpired", handle401);
  }, [logout]);

  useEffect(() => {
    document.title = "Pitchest AI";
  }, [location.pathname]);

  return (
    <>
      {!hideNavbar && <Navbar />}
      <Routes>
        <Route
          path="/"
          element={
            authLoading ? (
              <AppLoader message="Loading Pitchest..." />
            ) : isAuthenticated ? (
              <UserHome />
            ) : (
              <PublicHome />
            )
          }
        />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/signup" element={<SignupForm />} />
        <Route path="/login" element={<Login />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/activate-account/:token" element={<ActivateAccount />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/profile" element={<ProfileEdit />} />
        <Route path="/pitch-start" element={<PitchSessionStart />} />
        <Route path="/pitch" element={<PitchSession />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/sessions" element={<Sessions />} />
        <Route path="/achievements" element={<AchievementsPage />} />
        <Route path="/evaluation/:session_id" element={<EvaluationPage />} />
        <Route path="/contact" element={<ContactForm />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/checkout/result" element={<CheckoutResult />} />
        <Route path="/billing" element={<Billing />} />
        <Route path="/faqs" element={<Faqs />} />
        <Route path="/early-pitchers" element={<EarlyPitchers />} />
        <Route path="/check-api" element={<CheckAPI />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/admin/contact-messages" element={<AdminContactMessages />} />
        <Route path="/admin/early-pitchers" element={<AdminEarlyPitchers />} />
        <Route path="/admin/purchases" element={<AdminPurchases />} />
        <Route path="/admin/users" element={<AdminUsers />} />
        <Route path="/admin/sessions" element={<AdminSessions />} />
        <Route path="/admin/payments" element={<AdminPayments />} />
        <Route path="/admin/session-refunds" element={<AdminSessionRefunds />} />
        <Route path="/admin/discount-assignments" element={<AdminDiscountAssignments />} />
        <Route path="/admin/settings" element={<AdminSettings />} />
        <Route path="/circular" element={<SignupForm />} />
        <Route path="/undelta" element={<SignupForm />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!hideFooter && <Footer />}
      {tokenExpired && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 2000,
            pointerEvents: "none",
          }}
        >
          <Alert
            icon={<span style={{ fontSize: 28 }}>⚠️</span>}
            severity="warning"
            variant="filled"
            sx={{
              backgroundColor: "rgba(0,0,0,0.6)",
              color: "#fff",
              fontSize: 18,
              fontWeight: 600,
              borderRadius: 3,
              px: 4,
              py: 2,
              boxShadow: "0 0 20px rgba(0,0,0,0.5)",
              pointerEvents: "auto",
            }}
          >
            {t("session_expired")}
          </Alert>
        </Box>
      )}
    </>
  );
};

const App = () => (
  <SettingsProvider>
    <Router>
      <ScrollToTop />
      <AppLayout />
    </Router>
  </SettingsProvider>
);

export default App;
